import type { ForwardedRef, ReactElement } from 'react';
import React, { cloneElement, forwardRef } from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { StyleSheet, View, type ViewInstance } from 'react-native';

import { useResponsive } from '../../../hooks';

import type { ChipMenuProps, ChipProps } from '../Chip.types';

export const Spacer = () => {
  const { match } = useResponsive();

  return <View style={match([styles.spacerXS, styles.spacerSM])} />;
};

const isFirst = (index: number): boolean => index === 0;

export type ChipItemProps = ReactElement<ChipProps & Readonly<{ ref: React.Ref<unknown> }>>;

export const Menu = forwardRef<ViewInstance, ChipMenuProps>(
  (props: ChipMenuProps, ref: ForwardedRef<ViewInstance>): ReactElement => {
    const { children, value: selectedValue, onChange, style } = props;
    const flatChildren = flattenChildren(children);

    return (
      <View ref={ref} style={[styles.default, style]} accessibilityRole="radiogroup">
        {flatChildren.map((child, index) => {
          const item = child as ChipItemProps;

          return (
            <View style={styles.item} key={`radio-item-${index}`}>
              {isFirst(index) ? undefined : <Spacer />}
              {cloneElement(item, {
                onChange,
                selectedValue,
                accessibilityRole: 'radio',
                accessibilityState: {
                  checked: item.props.value === selectedValue,
                },
              })}
            </View>
          );
        })}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  item: { flexDirection: 'row' },
  default: {
    width: '100%',
    flexDirection: 'row',
  },
  spacerXS: { width: 8 },
  spacerSM: { width: 12 },
});
